import DOMPurify from "dompurify";
import { marked } from "marked";

import type { RetrievedChunkSnapshot } from "../db/types";
import { CITATION_RE, parseCitationMatch } from "./citations";
import { tr } from "../i18n/i18n.svelte";
import "./linkTargets";

marked.setOptions({ gfm: true, breaks: true });

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Private-use Unicode brackets — never appear in DPA text or markdown syntax,
// so they pass through marked untouched and let us round-trip citations safely.
const PH_OPEN = "";
const PH_CLOSE = "";
const PLACEHOLDER_RE = /(\d+)/g;

function citationSpan(
  refs: RetrievedChunkSnapshot[],
  numbers: number[],
  quote: string | null,
): string {
  const label = numbers.join(", ");
  const tooltipParts = refs.map(
    (r) => `${r.docName} p.${r.page}${r.section ? " — " + r.section : ""}`,
  );
  if (quote) tooltipParts.push(tr({ fr: `« ${quote} »`, en: `“${quote}”` }));
  const tooltip = tooltipParts.join(" | ");
  const dataRefs = numbers.join(",");
  const quoteAttr = quote ? ` data-quote="${escapeAttr(quote)}"` : "";
  return `<span class="citation" data-refs="${dataRefs}"${quoteAttr} title="${escapeAttr(tooltip)}">[${escapeAttr(label)}]</span>`;
}

/** Render the assistant's markdown content to safe HTML, replacing [N] / [N: "quote"]
 *  markers with citation spans.
 *
 *  Strategy: extract citations BEFORE running marked, substitute each with a private-
 *  use Unicode placeholder, parse markdown, then swap placeholders back to real spans.
 *  This prevents marked from treating an indented citation as a code block (e.g. when
 *  the LLM puts `[1: "..."]` on its own line under a bullet).
 */
export function renderMarkdownWithCitations(
  content: string,
  retrieved: RetrievedChunkSnapshot[] | null | undefined,
): string {
  if (!retrieved || retrieved.length === 0) {
    return DOMPurify.sanitize(
      marked.parse(content, { async: false }) as string,
    );
  }

  const byNumber = new Map(retrieved.map((r) => [r.number, r]));
  const placeholders: string[] = [];

  const re = new RegExp(CITATION_RE.source, "g");
  const withPlaceholders = content.replace(re, (...args) => {
    const match = args as unknown as RegExpMatchArray;
    const parsed = parseCitationMatch(match);
    const refs = parsed.refs
      .map((n) => byNumber.get(n))
      .filter((r): r is RetrievedChunkSnapshot => !!r);
    if (refs.length === 0) return args[0] as string;

    const idx = placeholders.length;
    placeholders.push(citationSpan(refs, parsed.refs, parsed.quote));
    return `${PH_OPEN}${idx}${PH_CLOSE}`;
  });

  const rawHtml = marked.parse(withPlaceholders, { async: false }) as string;

  const withCitations = rawHtml.replace(PLACEHOLDER_RE, (_, idxStr: string) => {
    return placeholders[Number(idxStr)] ?? "";
  });

  return DOMPurify.sanitize(withCitations);
}
